import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector, useDispatch} from "react-redux";
import Sidebar from "./sidebar";
import DocumentTable from "./DocumentTable";
import ImportModal from "./ImportModal";
import "./referentiel.css";
import {
    RequestDeleteDocument,
    RequestGetDocumentsAndFolders,
    RequestUploadDocument,
} from "../../Actions/referentiel.action";
import { fetchCommercialUsage, openCommercialGateModal } from "../../Actions/commercialControl.action";
import {download_referentiel_document} from "../../Services/referentiel.service";
import {Button, message, Empty, Input, Spin} from "antd";
import {useTranslation} from "react-i18next";
import {Database, FileText, FolderOpen, LockKeyhole, Plus, Search} from "lucide-react";
import {
    canUseRepository,
    buildLockedCommercialDecision,
    hasCommercialCapabilitiesLoaded,
} from "../../utils/commercialCapabilities";

const UNCATEGORIZED_KEY = "__uncategorized__";

const sortFolders = (items = []) =>
    [...items].sort((a, b) => {
        if (a.key === UNCATEGORIZED_KEY) return -1;
        if (b.key === UNCATEGORIZED_KEY) return 1;
        return String(a.title || a.key).localeCompare(String(b.title || b.key), undefined, {
            sensitivity: "base",
        });
    });

export default function Referentiel() {
    const dispatch = useDispatch();
    const {t} = useTranslation("referentiel");
    const folders = useSelector((state) => state.files.folders);
    const documents = useSelector((state) => state.files.documents);
    const commercialControl = useSelector((state) => state.commercialControl || {});
    const commercialCapabilities = commercialControl.capabilities || {};
    const repositoryAllowed = canUseRepository(commercialCapabilities);
    const capabilitiesLoaded = hasCommercialCapabilitiesLoaded(commercialCapabilities);
    const [selectedFolderKey, setSelectedFolderKey] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showImportModal, setShowImportModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const orderedFolders = useMemo(() => sortFolders(folders), [folders]);
    const openRepositoryOffers = useCallback(() => {
        dispatch(openCommercialGateModal(buildLockedCommercialDecision("repository", {
            ctaFr: "Voir les offres",
            ctaEn: "View plans",
            bodyFr: "Le repository permet de stocker et réutiliser vos analyses et contrats.",
            bodyEn: "The repository lets you store and reuse your analyses and contracts.",
        })));
    }, [dispatch]);

    useEffect(() => {
        if (!repositoryAllowed) return;
        dispatch(RequestGetDocumentsAndFolders());
    }, [dispatch, repositoryAllowed]);

    useEffect(() => {
        if (commercialControl.usageSnapshot || commercialControl.isLoadingUsage) return;
        dispatch(fetchCommercialUsage()).catch(() => {});
    }, [commercialControl.isLoadingUsage, commercialControl.usageSnapshot, dispatch]);

    useEffect(() => {
        // ✅ Sélectionner le premier folder seulement s'il y en a
        if (!selectedFolderKey && orderedFolders.length > 0) {
            setSelectedFolderKey(orderedFolders[0].key);
        }
        // ✅ Si tous les folders ont été supprimés, réinitialiser
        if (selectedFolderKey && orderedFolders.length === 0) {
            setSelectedFolderKey(null);
        }
        if (
            selectedFolderKey &&
            orderedFolders.length > 0 &&
            !orderedFolders.some((folder) => folder.key === selectedFolderKey)
        ) {
            setSelectedFolderKey(orderedFolders[0].key);
        }
    }, [orderedFolders, selectedFolderKey]);

    if (!capabilitiesLoaded && commercialControl.isLoadingUsage) {
        return (
            <div className="referentiel-container">
                <section className="referentiel-empty-shell" style={{ width: "100%" }}>
                    <Spin />
                </section>
            </div>
        );
    }

    if (!repositoryAllowed) {
        return (
            <div className="referentiel-container referentiel-container--locked">
                <div className="referentiel-content">
                    <section className="referentiel-locked-hero">
                        <div className="referentiel-locked-copy">
                            <span className="referentiel-eyebrow">
                                <LockKeyhole size={14} />
                                {t("commercial.lockedEyebrow", "Fonctionnalite premium")}
                            </span>
                            <h1>{t("commercial.lockedTitle", "Referentiel verrouille")}</h1>
                            <p>
                                {t(
                                    "commercial.lockedBody",
                                    "Centralisez vos contrats de référence et documents d'appui dans un espace partagé, puis rattachez-les aux analyses quand votre plan l'autorise."
                                )}
                            </p>
                            <p className="referentiel-locked-value">
                                {t(
                                    "commercial.lockedValue",
                                    "Le repository permet de stocker et réutiliser vos analyses et contrats."
                                )}
                            </p>
                            <div className="referentiel-locked-actions">
                                <Button
                                    type="primary"
                                    className="verdictia-primary-btn referentiel-locked-cta"
                                    icon={<LockKeyhole size={15} />}
                                    onClick={openRepositoryOffers}
                                >
                                    {t("commercial.lockedCta", "Voir les offres")}
                                </Button>
                                <span>{t("commercial.lockedHint", "Les fichiers clients ne sont pas affichés avec le plan actuel.")}</span>
                            </div>
                        </div>

                        <div className="referentiel-locked-preview" aria-hidden="true">
                            <div className="referentiel-preview-sidebar">
                                <span className="referentiel-preview-title">
                                    <Database size={15} />
                                    {t("commercial.previewLibrary", "Bibliotheque")}
                                </span>
                                {["Contrats cadres", "DPA / RGPD", "Annexes SLA"].map((label) => (
                                    <span key={label} className="referentiel-preview-folder">
                                        <FolderOpen size={14} />
                                        {label}
                                    </span>
                                ))}
                            </div>
                            <div className="referentiel-preview-list">
                                {[1, 2, 3].map((item) => (
                                    <div key={item} className="referentiel-preview-file">
                                        <FileText size={16} />
                                        <span />
                                        <small />
                                    </div>
                                ))}
                            </div>
                            <div className="referentiel-preview-lock">
                                <LockKeyhole size={18} />
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    const handleDelete = (id) => {
        dispatch(RequestDeleteDocument(id));
    };

    const handleDownload = (doc) => {
        const filename = `${doc.title}.${doc.file_extension}`;

        download_referentiel_document(doc.id, filename).catch(() => {
            message.error(t("errors.download"));
        });
    };

    const handleUpload = async (formData) => {
        try {
            setUploading(true);
            await dispatch(RequestUploadDocument(
                formData,
                (folder) => setSelectedFolderKey(folder),
                setShowImportModal
            ));
        } finally {
            setUploading(false);
        }
    };

    // ✅ Fonction pour normaliser les folders
    const normalizeFolder = (folder) => {
        const trimmed = folder?.trim();
        return trimmed || UNCATEGORIZED_KEY;
    };

    const getFolderTitle = (folderKey) =>
        folderKey === UNCATEGORIZED_KEY ? t("newFolder.uncategorized") : folderKey;

    const getDocumentsByFolder = (folderKey) =>
        documents.filter((doc) => normalizeFolder(doc.folder) === folderKey);

    const folderStats = folders.reduce((acc, folder) => {
        acc[folder.key] = getDocumentsByFolder(folder.key).length;
        return acc;
    }, {});

    const selectedFolder = selectedFolderKey || orderedFolders[0]?.key || UNCATEGORIZED_KEY;
    const selectedDocuments = getDocumentsByFolder(selectedFolder);
    const filteredDocuments = selectedDocuments.filter((doc) => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return true;

        return [doc.title, doc.file_extension, doc.created_at].some((value) =>
            String(value || "").toLowerCase().includes(query)
        );
    });

    return (
        <div className="referentiel-container">
            <header className="referentiel-page-header">
                <div className="referentiel-page-copy">
                    <h1>{t("overview.title", "Référentiel de données")}</h1>
                    <p>
                        {t(
                            "overview.subtitle",
                            "Classez les documents de référence par dossier et retrouvez vite la bonne source pour vos analyses."
                        )}
                    </p>
                    <div className="referentiel-meta" aria-label={t("overview.metrics", "Indicateurs")}>
                        <span>{documents.length} {t("overview.files", "fichiers")}</span>
                        <span aria-hidden="true">·</span>
                        <span>{orderedFolders.length} {t("overview.folders", "dossiers")}</span>
                    </div>
                </div>

                <div className="referentiel-page-actions">
                    <Input
                        className="referentiel-search"
                        prefix={<Search size={15}/>}
                        allowClear
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder={t("actions.search", "Rechercher un fichier")}
                        aria-label={t("actions.search", "Rechercher un fichier")}
                    />
                    <Button
                        type="primary"
                        className="verdictia-primary-btn referentiel-import-button"
                        icon={<Plus size={16}/>}
                        onClick={() => setShowImportModal(true)}
                    >
                        {t("actions.import")}
                    </Button>
                </div>
            </header>

            <Sidebar
                folders={orderedFolders}
                selectedKey={selectedFolder}
                onSelectFolder={setSelectedFolderKey}
                folderStats={folderStats}
            />
            <div className="referentiel-content">
                {orderedFolders.length === 0 ? (
                    <section className="referentiel-empty-shell">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={t("messages.noDocuments", "Aucun document pour le moment")}
                        >
                            <p>{t("messages.uploadFirst", "Importez votre premier document pour commencer")}</p>
                        </Empty>
                    </section>
                ) : (
                    <section className="referentiel-workspace">
                    <div className="referentiel-workspace-header">
                        <div className="folder-heading">
                            <FolderOpen size={18}/>
                            <h2>{getFolderTitle(selectedFolder)}</h2>
                            <span className="folder-document-count">
                                {selectedDocuments.length} {t("overview.files", "fichiers")}
                            </span>
                        </div>
                    </div>

                    <DocumentTable
                        documents={filteredDocuments}
                        onDownload={handleDownload}
                        onDelete={handleDelete}
                    />
                </section>
                )}
            </div>

            <ImportModal
                visible={showImportModal}
                folders={orderedFolders}
                onClose={() => setShowImportModal(false)}
                onSubmit={handleUpload}
                loading={uploading}
            />
        </div>
    );
}
